// Comments on a Drive file — the client for Drive BFF's /api/items/:id/comments
// family. Identical file in every app that shows comments; change them together:
//   EnterpriseDrive/web/src/features/drive/comments/
//   EnterpriseDocs/web/src/features/drive-file/
//   EnterpriseSheet/web/src/features/drive-file/
//   EnterprisePresentation/react-editor/src/features/drive-file/
//   EnterprisePdf/web/src/features/pdf/comments/
// Self-contained on purpose (only the Drive BFF URL from the environment), so
// the copies never drift on each app's own Drive client.

const baseUrl = (import.meta.env as Record<string, string | undefined>).VITE_DRIVE_BFF_BASE_URL ?? "";
const enc = encodeURIComponent;

export type CommentRole = "owner" | "editor" | "viewer";

export interface DriveComment {
    id: string;
    itemId: string;
    /** Null for a thread root; replies point at the root. */
    parentId: string | null;
    authorEmail: string;
    authorName: string;
    /** Plain text — render as text, never as HTML. Empty once deleted. */
    body: string;
    /** Where in the file: "p:0;12;0", "Sheet1!B4", "slide:3;id=…", "page:2". */
    anchor?: string | null;
    /** The text the comment refers to. */
    quote?: string | null;
    isResolved: boolean;
    resolvedBy?: string | null;
    resolvedAt?: string | null;
    createdAt: string;
    editedAt?: string | null;
    isDeleted: boolean;
    canEdit: boolean;
    canDelete: boolean;
    /** Roots only. */
    canResolve: boolean;
}

export interface CommentThread {
    root: DriveComment;
    replies: DriveComment[];
    lastActivityAt: string;
}

export interface CommentList {
    threads: CommentThread[];
    openCount: number;
    resolvedCount: number;
    myRole: CommentRole;
    canComment: boolean;
}

export interface CommentCount {
    open: number;
    resolved: number;
}

export interface CommentPerson {
    email: string;
    role: CommentRole;
}

export interface CommentPeople {
    people: CommentPerson[];
    /** Owners and editors can share the file with someone they mention. */
    canShare: boolean;
}

export class CommentsApiError extends Error {
    readonly status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = "CommentsApiError";
        this.status = status;
    }
}

/** 409 — the comment mentions people who can't open the file. */
export class MentionAccessError extends CommentsApiError {
    readonly missing: string[];

    constructor(message: string, missing: string[]) {
        super(message, 409);
        this.name = "MentionAccessError";
        this.missing = missing;
    }
}

// Bare reason phrases ASP.NET puts in `title` — our own wording is clearer.
const GENERIC_TITLES = /^(not found|forbidden|unauthorized|bad request|conflict|internal server error)$/i;

async function call<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body !== undefined) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers, credentials: "omit" });
    } catch {
        throw new CommentsApiError("Can't reach Drive — check your connection.", 0);
    }
    if (!res.ok) {
        let message = res.status === 404
            ? "Drive couldn't find this file, or you no longer have access to it."
            : res.status === 403
                ? "You don't have permission to do that."
                : res.status === 401
                    ? "Your session has expired — please sign in again."
                    : `Drive request failed (${res.status}).`;
        let missing: string[] | null = null;
        try {
            const body = (await res.json()) as { title?: string; detail?: string; missing?: unknown };
            const title = body.title?.trim();
            message = body.detail?.trim() || (title && !GENERIC_TITLES.test(title) ? title : "") || message;
            if (Array.isArray(body.missing)) missing = body.missing.filter((m): m is string => typeof m === "string");
        } catch {
            /* not a JSON body — keep our wording */
        }
        if (res.status === 409 && missing && missing.length > 0) throw new MentionAccessError(message, missing);
        throw new CommentsApiError(message, res.status);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

export interface NewComment {
    body: string;
    anchor?: string | null;
    quote?: string | null;
    /** Post even though someone mentioned can't open the file. */
    ignoreMissingAccess?: boolean;
}

export interface CommentText {
    body: string;
    ignoreMissingAccess?: boolean;
}

const post = (body?: unknown): RequestInit => ({ method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

export const commentsApi = {
    /** Threads, most recently active first. */
    list: (itemId: string, includeResolved: boolean, token: string | undefined) =>
        call<CommentList>(`/api/items/${enc(itemId)}/comments?includeResolved=${includeResolved}`, token),

    count: (itemId: string, token: string | undefined) =>
        call<CommentCount>(`/api/items/${enc(itemId)}/comments/count`, token),

    create: (itemId: string, comment: NewComment, token: string | undefined) =>
        call<DriveComment>(`/api/items/${enc(itemId)}/comments`, token, post(comment)),

    reply: (commentId: string, text: CommentText, token: string | undefined) =>
        call<DriveComment>(`/api/comments/${enc(commentId)}/replies`, token, post(text)),

    edit: (commentId: string, text: CommentText, token: string | undefined) =>
        call<DriveComment>(`/api/comments/${enc(commentId)}`, token, { method: "PATCH", body: JSON.stringify(text) }),

    remove: (commentId: string, token: string | undefined) =>
        call<void>(`/api/comments/${enc(commentId)}`, token, { method: "DELETE" }),

    resolve: (commentId: string, token: string | undefined) =>
        call<DriveComment>(`/api/comments/${enc(commentId)}/resolve`, token, post()),

    reopen: (commentId: string, token: string | undefined) =>
        call<DriveComment>(`/api/comments/${enc(commentId)}/reopen`, token, post()),

    /** The owner plus everyone the file is shared with — the @mention list. */
    people: async (itemId: string, token: string | undefined): Promise<CommentPeople> => {
        const s = await call<{ ownerEmail: string; canManage: boolean; people: Array<{ email: string; role: string }> }>(
            `/api/items/${enc(itemId)}/permissions`,
            token
        );
        const people: CommentPerson[] = [{ email: s.ownerEmail.toLowerCase(), role: "owner" }];
        for (const p of s.people) {
            const email = p.email.toLowerCase();
            if (!people.some((x) => x.email === email)) people.push({ email, role: p.role === "editor" ? "editor" : "viewer" });
        }
        return { people, canShare: s.canManage };
    },

    /** Shares the file with people so they can open what they were mentioned in. */
    shareAsViewers: (itemId: string, emails: string[], token: string | undefined) =>
        call<unknown>(`/api/items/${enc(itemId)}/permissions`, token, post({ emails, role: "viewer", allowDownload: true, source: "drive" }))
};

// "@" + an email, not itself preceded by an email character — the same rule
// the BFF uses, so what's highlighted is exactly what it treats as a mention.
const MENTION_RE = /(?<![A-Za-z0-9._%+\-@])@([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)/g;

/** Text and @mention runs of a body, for rendering as text nodes. */
export function splitMentions(body: string): Array<{ text: string; mention: string | null }> {
    const out: Array<{ text: string; mention: string | null }> = [];
    let last = 0;
    for (const m of body.matchAll(MENTION_RE)) {
        const start = m.index ?? 0;
        if (start > last) out.push({ text: body.slice(last, start), mention: null });
        out.push({ text: m[0], mention: (m[1] ?? "").toLowerCase() });
        last = start + m[0].length;
    }
    if (last < body.length) out.push({ text: body.slice(last), mention: null });
    return out;
}

/** "anna.kumar@x.com" → "Anna Kumar". */
export function nameFromEmail(email: string): string {
    const local = email.split("@")[0] ?? email;
    const words = local.split(/[._-]+/).filter(Boolean);
    if (words.length === 0) return email;
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** "just now", "5 min ago", "3 h ago", "Yesterday", then a short date. */
export function formatCommentTime(iso: string, now: Date = new Date()): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const seconds = Math.round((now.getTime() - d.getTime()) / 1000);
    if (seconds < 45) return "just now";
    if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min ago`;
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOf(now) - startOf(d)) / 86_400_000);
    if (days === 0) return `${Math.round(seconds / 3600)} h ago`;
    if (days === 1) return "Yesterday";
    return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: d.getFullYear() === now.getFullYear() ? undefined : "numeric"
    });
}

export function commentErrorText(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}
